import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Inject,
  Output,
  EventEmitter,
  AfterViewInit,
  ElementRef,
  ViewChildren,
  QueryList,
} from '@angular/core';
import {
  getApplicationStateColor,
  getApplicationStateLabel,
} from '../../services/application/application.service';
import { PublicServicesService } from '../../services/public-services/public-services.service';
import { ApplicationsService } from '../../services/application/applications.service';
import { ToastService } from '../../services/toast/toast.service';
import { finalize, Subscription } from 'rxjs';
import { PaymentService } from '../../services/public-services/payment.service';
import { IEnvironment } from '../../models/environment.model';
import {
  IApplicationDetails,
  IFeedbackResponseDetails,
  IOfficerAttachment,
} from '../../models/application-details.model';
import { ICertificateDownload } from '../../models/certificate-download.model';
import { IApplicationFormData } from '../../models/application-form-data.model';
import { IServiceTranslation } from '../../models/service-translation.model';
import { ServiceTranslationMergerService } from '../../services/service-translation-merger/service-translation-merger.service';
import { applicationFormBuildPreviewDataTranslated } from '../../../utils/utils/application-form-translated.util';
import { performFormTranslations } from '../../../utils/utils/form-translations.util';
import {
  FormlyFieldConfig,
  FormlyFormBuilder,
  FormlyFormOptions,
} from '@ngx-formly/core';
import { FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

declare const IremboPay: any;

@Component({
  standalone: false,
  selector: 'irembogov-child-application-viewer',
  templateUrl: './irembogov-child-application-viewer.component.html',
  styleUrls: ['./irembogov-child-application-viewer.component.scss'],
})
export class IrembogovChildApplicationViewerComponent
  implements OnInit, OnChanges, AfterViewInit
{
  @Input() parentApplicationId!: string;
  @Input() locale = '';
  @Input() viewFullDetails = false;
  @Input() downloadingRecieptByOtp = false;
  @Input() otp: string | null = null; // Initialize to null so empty string triggers change
  @Input() downloadingAttachmentsByOtp = false;
  @Input() parentApplicationType = '';

  @Output() downloadReceiptbyOtp: EventEmitter<string> = new EventEmitter();
  @Output()
  downloadAttachmentsbyOtp: EventEmitter<IOfficerAttachment> =
    new EventEmitter();
  @Output() requestOtpForDetails: EventEmitter<string> = new EventEmitter();

  isLoading = false;
  showDropdown = true;
  downloadingReceipt = false;
  isDownloadingCertificate = false;
  downloadedAttachment: ICertificateDownload | undefined;
  public environment: IEnvironment;

  @ViewChildren('certificateDropdown')
  certificateDropdowns!: QueryList<ElementRef>;

  private subscriptions = new Subscription();

  childApplications?: IApplicationDetails[] = [];

  // View Details state management
  expandedChildIds = new Set<string>();
  pendingOtpChildIds = new Set<string>();
  childDetailsMap = new Map<string, any>();
  childSummariesMap = new Map<string, IApplicationFormData[]>();
  loadingChildDetails = new Set<string>();
  childServiceTranslations = new Map<string, IServiceTranslation>();
  private childActiveFeedbacks = new Map<string, IFeedbackResponseDetails[]>();

  constructor(
    private serviceService: PublicServicesService,
    private applicationsService: ApplicationsService,
    private toastService: ToastService,
    private paymentService: PaymentService,
    private serviceTranslationMerger: ServiceTranslationMergerService,
    private formlyBuilder: FormlyFormBuilder,
    private translateService: TranslateService,
    @Inject('environment') environment: IEnvironment
  ) {
    this.environment = environment;
  }

  ngOnInit(): void {
    this.getApplicationByParentID();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Watch for OTP changes - when OTP is set (even empty string), fetch pending child details
    if (
      changes['otp']?.currentValue !== undefined && // Changed: Accept empty string too
      !changes['otp']?.firstChange
    ) {
      // OTP was just set - expand any children that were waiting for OTP
      this.pendingOtpChildIds.forEach(appNumber => {
        const child = this.childApplications?.find(
          app => app.applicationNumber === appNumber
        );

        // Set loading state FIRST to prevent error flash
        if (child && !this.childDetailsMap.has(appNumber)) {
          this.loadingChildDetails.add(appNumber);
        }

        // Then expand the child (UI will show loading spinner, not error)
        this.expandedChildIds.add(appNumber);

        // Finally, start fetching
        if (child && !this.childDetailsMap.has(appNumber)) {
          this.fetchChildApplicationDetails(child);
        }
      });

      // Clear pending OTP set
      this.pendingOtpChildIds.clear();

      // Also check if any children are already expanded and need details
      this.childApplications?.forEach(app => {
        const appNumber = app.applicationNumber;
        if (
          this.expandedChildIds.has(appNumber) &&
          !this.childDetailsMap.has(appNumber) &&
          !this.loadingChildDetails.has(appNumber)
        ) {
          // This child is expanded but doesn't have details yet - fetch now
          this.fetchChildApplicationDetails(app);
        }
      });
    }
  }

  ngAfterViewInit(): void {
    // Ensure dropdown functionality is initialized after view is fully loaded
    setTimeout(() => {
      this.initializeDropdowns();
    }, 0);
  }

  private initializeDropdowns(): void {
    // Check if Bootstrap is available
    if (typeof window !== 'undefined' && (window as any).bootstrap) {
      const bootstrap = (window as any).bootstrap;
      this.certificateDropdowns.forEach(dropdown => {
        new bootstrap.Dropdown(dropdown.nativeElement);
      });
    }
  }

  getActiveFeedbacks(applicationNumber: string): IFeedbackResponseDetails[] {
    return this.childActiveFeedbacks.get(applicationNumber) ?? [];
  }

  trackChildFeedback(_: number, feedback: IFeedbackResponseDetails): string {
    const identifier = feedback.applicationNumber ?? feedback.serviceCode ?? '';
    return `${identifier}-${feedback.comment ?? ''}`;
  }

  getApplicationByParentID() {
    this.isLoading = true;
    this.serviceService
      .getApplicationByParentID(this.parentApplicationId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: response => {
          this.childApplications = response.data;
          this.childActiveFeedbacks.clear();
          this.childApplications?.forEach(app => {
            const activeFeedbacks =
              app.applicationFeedbacks?.feedbackResponseDetails?.filter(
                feedback => feedback.activeFeedback === true
              ) ?? [];

            this.childActiveFeedbacks.set(
              app.applicationNumber,
              activeFeedbacks
            );
          });
        },
        error: err => {
          this.toastService.show({
            body: err.error.message,
            type: 'error',
            delay: 5000,
          });
        },
      });
  }

  getApplicationStatusToColor(applicationStatus: string): string {
    return getApplicationStateColor(applicationStatus);
  }

  getApplicationStatusToLabel(applicationStatus: string): string {
    return getApplicationStateLabel(applicationStatus);
  }

  getApplicationStatusPulseToBackgroundColor(applicationStatus: string) {
    const color = getApplicationStateColor(applicationStatus);
    return `bg-${color}`;
  }

  convertDate(dateString: string): Date {
    // Split the string into date and time components using 'T'
    const [datePart, timePart] = dateString.split('T');
    const [day, month, year] = datePart.split('/').map(Number);
    const [hours = 0, minutes = 0, seconds = 0] = timePart
      ? timePart.split(':').map(Number)
      : [0, 0, 0];
    return new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds));
  }

  isSelectedApplicationFree(application: IApplicationDetails): boolean {
    return (
      application?.priceType === 'FREE' ||
      (application?.priceType === 'DYNAMIC' && Number(application?.price) === 0)
    );
  }

  downloadReceipt(applicationNumber: string | undefined) {
    if (applicationNumber !== undefined) {
      if (this.otp !== '') {
        this.downloadReceiptbyOtp.emit(applicationNumber);
      } else {
        this.downloadingReceipt = true;
        this.subscriptions.add(
          this.paymentService.getPaymentReceipt(applicationNumber).subscribe({
            next: res => {
              this.downloadingReceipt = false;
              const source =
                `data:` + res?.contentType + `;base64,${res?.file}`;
              const link = document.createElement('a');
              link.href = source;
              link.download = applicationNumber + `_payment_receipt.pdf`;
              link.click();
            },
            error: () => {
              this.downloadingReceipt = false;
              this.toastService.show({
                body: 'Error downloading payment receipt',
                type: 'error',
              });
            },
          })
        );
      }
    }
  }

  makePayment(merchant: string, billId: string) {
    this.getPaymentPublicKey(merchant).then(publicKey => {
      if (publicKey) {
        IremboPay.initiate({
          publicKey: publicKey,
          invoiceNumber: billId,
          locale: IremboPay.locale.EN,
          isTest: this.environment.IREMBOPAY_TEST,
          callback: (err: unknown, resp: Record<string, string>) => {
            if (!err) {
              IremboPay.closeModal();
              this.toastService.show({
                body: resp['message'],
                type: 'success',
              });

              // Wait a short delay for the payment to be processed on the server
              setTimeout(() => {
                // Refresh the list after payment completion
                this.refreshApplicationList();
              }, 2000); // 2-second delay to allow server-side processing
            }
          },
        });
      }
    });
  }

  refreshApplicationList() {
    this.isLoading = true;

    this.serviceService
      .getApplicationByParentID(this.parentApplicationId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: response => {
          // Update the list with new data
          this.childApplications = response.data;

          // Show success message for refresh
          this.toastService.show({
            body: 'Application status updated successfully',
            type: 'success',
          });
        },
        error: () => {
          this.toastService.show({
            body: 'Error updating application status. Please refresh the page.',
            type: 'error',
            delay: 5000,
          });
        },
      });
  }

  getPaymentPublicKey(merchant: string | undefined): Promise<any> {
    return new Promise((resolve, reject) => {
      if (merchant !== undefined) {
        this.subscriptions.add(
          this.paymentService.getPaymentPublicKey(merchant).subscribe({
            next: res => {
              const publicKey = res.data.publicKey;
              resolve(publicKey);
            },
          })
        );
      } else {
        reject();
      }
    });
  }

  applicationCertificates(
    application: IApplicationDetails
  ): IOfficerAttachment[] {
    return Object.values(application.officerAttachments ?? {}).flat();
  }

  downloadAttachment(attachment: IOfficerAttachment) {
    if (this.otp !== '') {
      this.downloadAttachmentsbyOtp.emit(attachment);
    } else {
      this.isDownloadingCertificate = true;
      this.subscriptions.add(
        this.serviceService
          .downloadAttachment(attachment.applicationId, attachment.fileName)
          .subscribe({
            next: res => {
              this.downloadedAttachment = {
                file: '',
                contentType:
                  res.headers.get('Content-Type') ?? 'application/pdf',
              };
              this.downloadBlob(
                attachment?.serviceName +
                  ' - ' +
                  attachment.applicationNumber +
                  attachment.fileName.split('.').pop(),
                res.body as Blob
              );
            },
            error: () => {
              this.toastService.show({
                body: 'Error downloading attachment',
                type: 'error',
              });
              this.isDownloadingCertificate = false;
            },
          })
      );
    }
  }

  handleFiledownload(
    base64Data: string,
    contentType: string,
    fileName: string
  ) {
    const blob = this.base64ToBlob(base64Data, contentType);
    this.downloadBlob(fileName, blob);
  }

  private base64ToBlob(str: string, type: string) {
    const content = atob(str);
    const buffer = new ArrayBuffer(content.length);
    const view = new Uint8Array(buffer);
    for (let n = 0; n < content.length; n++) {
      view[n] = content.charCodeAt(n);
    }
    const blob = new Blob([buffer], { type: type });
    return blob;
  }

  downloadBlob(fileName: string, blob: Blob): void {
    const anchor = window.document.createElement('a');
    anchor.href = window.URL.createObjectURL(blob);
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(anchor.href);
    this.isDownloadingCertificate = false;
  }

  shouldShowViewDetailsButton(): boolean {
    return this.parentApplicationType === 'JOURNEY_V3';
  }

  isChildExpanded(applicationNumber: string): boolean {
    return this.expandedChildIds.has(applicationNumber);
  }

  toggleChildDetails(applicationNumber: string): void {
    if (this.expandedChildIds.has(applicationNumber)) {
      // Collapse - just remove from expanded set
      this.expandedChildIds.delete(applicationNumber);
    } else {
      // Set loading state FIRST if we'll be fetching (prevents error flash)
      if (!this.childDetailsMap.has(applicationNumber)) {
        this.loadingChildDetails.add(applicationNumber);
      }

      // Expand the child
      this.expandedChildIds.add(applicationNumber);

      // Fetch details if not already cached
      // If OTP is required, the error handler will trigger parent to show modal
      if (!this.childDetailsMap.has(applicationNumber)) {
        const child = this.childApplications?.find(
          app => app.applicationNumber === applicationNumber
        );
        if (child) {
          this.fetchChildApplicationDetails(child);
        } else {
          console.error(
            '[Child] Child application not found in list:',
            applicationNumber
          );
          this.loadingChildDetails.delete(applicationNumber); // Remove loading state if error
        }
      }
    }
  }

  fetchChildApplicationDetails(child: IApplicationDetails): void {
    const applicationNumber = child.applicationNumber;

    // Note: loadingChildDetails should already have this applicationNumber
    // (added by toggleChildDetails or ngOnChanges before calling this method)

    this.subscriptions.add(
      this.applicationsService
        .getApplicationDetailsByOTP(applicationNumber, this.otp || '')
        .subscribe({
          next: res => {
            // Cache the details
            this.childDetailsMap.set(applicationNumber, res.data);

            // Fetch service translations
            const serviceId = res.data?.['serviceId'] as string;
            if (serviceId) {
              this.getChildServiceTranslation(applicationNumber, serviceId);
            } else {
              // Build summary without translations
              this.buildChildSummary(applicationNumber, res.data);
            }
          },
          error: err => {
            // Check if error is due to OTP requirement
            if (err?.error?.responseCode === 'APPLICATION_OTP_REQUIRED') {
              // Remove from expanded and loading states
              this.expandedChildIds.delete(applicationNumber);
              this.loadingChildDetails.delete(applicationNumber);
              // Add to pending OTP list
              this.pendingOtpChildIds.add(applicationNumber);
              // Emit event to parent to request OTP
              this.requestOtpForDetails.emit(applicationNumber);
              return;
            }

            // Remove loading state on error
            this.loadingChildDetails.delete(applicationNumber);
            this.toastService.show({
              body: err?.error?.message || 'Unable to load application details',
              type: 'error',
              delay: 5000,
            });
            // Remove from expanded on error
            this.expandedChildIds.delete(applicationNumber);
          },
        })
    );
  }

  getChildServiceTranslation(
    applicationNumber: string,
    serviceId: string
  ): void {
    this.subscriptions.add(
      this.serviceTranslationMerger
        .loadAndMergeServiceTranslation(
          serviceId,
          this.locale,
          this.translateService
        )
        .subscribe({
          next: response => {
            // Cache translations
            this.childServiceTranslations.set(applicationNumber, response);

            // Build summary with translations
            const details = this.childDetailsMap.get(applicationNumber);
            if (details) {
              this.buildChildSummary(applicationNumber, details);
            }
          },
          error: () => {
            // Continue without translations
            const details = this.childDetailsMap.get(applicationNumber);
            if (details) {
              this.buildChildSummary(applicationNumber, details);
            }
          },
        })
    );
  }

  buildChildSummary(
    applicationNumber: string,
    details: Record<string, unknown>
  ): void {
    const applicationSummary = details['applicationSummary'] as Record<
      string,
      unknown
    >;
    const formDefinition = details['formDefinition'] as Record<string, unknown>;
    const fields = formDefinition?.['fields'] as FormlyFieldConfig[];

    if (!fields || !applicationSummary) {
      console.error('Missing form definition or application summary');
      // Remove loading state even on error
      this.loadingChildDetails.delete(applicationNumber);
      return;
    }

    // Get translations for this child
    const serviceTranslations =
      this.childServiceTranslations.get(applicationNumber);

    // Translate form fields
    const translatedLayout = performFormTranslations(
      {
        translatedLayout: structuredClone(fields),
        layout: fields,
        serviceTranslations: serviceTranslations,
        translateService: this.translateService,
      },
      this.translateService.currentLang || 'en'
    );

    // Build a temporary Formly form to trigger all expression evaluations
    const form = new FormGroup({});
    const currentLang = this.translateService.currentLang || 'en';
    const options: FormlyFormOptions = {
      formState: {
        getTranslation: (key: string) => {
          const translated = this.translateService.instant(key);
          if (translated !== key) {
            return translated;
          }
          const localeTranslations = serviceTranslations?.[currentLang] as
            | Record<string, string>
            | undefined;
          return localeTranslations?.[key] || key;
        },
      },
    };

    // Build form to evaluate expressions
    this.formlyBuilder.buildForm(
      form,
      translatedLayout,
      applicationSummary,
      options
    );

    // Build the summary
    const summary = applicationFormBuildPreviewDataTranslated(
      translatedLayout,
      applicationSummary,
      serviceTranslations,
      false,
      this.translateService
    );

    // Cache the built summary
    this.childSummariesMap.set(applicationNumber, summary);

    // NOW remove from loading state - we're truly done!
    this.loadingChildDetails.delete(applicationNumber);
  }
}
